/* eslint-disable @typescript-eslint/no-loss-of-precision */
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { useEffect, useMemo, useState } from 'react';
import { Control, Controller, Path } from 'react-hook-form';

import { formatLatLng } from '../../helpers/format';
import { Feature } from '../../helpers/mapbox';
import { useForwardGeocode } from '../../hooks';

interface IProps<T> {
  control: Control<T>;
  disabled?: boolean;
  name: Path<T>;
  placeholder?: string;
}

export function LocationAutocomplete<T>({
  control,
  disabled,
  name,
  placeholder,
}: IProps<T>) {
  const { geocode, loading: isLoading } = useForwardGeocode();
  const [value, setValue] = useState<Feature | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Feature[]>([]);

  const lazyQueryThrottled = useMemo(
    () =>
      throttle((query: string) => geocode(query), 50, {
        trailing: false,
        leading: true,
      }),
    [geocode],
  );

  useEffect(() => {
    if (inputValue.length < 4) {
      return;
    }

    let active = true;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    lazyQueryThrottled(inputValue)?.then((results) => {
      if (active) {
        let newOptions: Feature[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results?.features) {
          newOptions = [...newOptions, ...results.features];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, lazyQueryThrottled, value]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          id="mapbox-geocoder-service"
          data-testid="mapbox-geocoder-service"
          disabled={disabled}
          autoComplete
          filterOptions={(x) => x}
          filterSelectedOptions
          getOptionLabel={(option) => option.properties.full_address}
          includeInputInList
          loading={isLoading}
          options={options}
          sx={{ width: '100%' }}
          value={value}
          onChange={(event: any, newValue: Feature | null) => {
            setOptions(newValue ? [newValue, ...options] : options);
            setValue(newValue);
            field.onChange({
              address: newValue?.properties.full_address,
              name:
                newValue?.properties.place_formatted ||
                newValue?.properties.name,
              point: {
                type: 'Point',
                coordinates: [
                  newValue?.geometry.coordinates[1],
                  newValue?.geometry.coordinates[0],
                ],
              },
            });
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: null,
                startAdornment: <SearchOutlined sx={{ color: 'grey.50' }} />,
              }}
              error={Boolean(error)}
              helperText={error?.message}
              placeholder={placeholder}
              fullWidth
            />
          )}
          renderOption={(props, option) => {
            // Highlight text
            const matches = match(option.properties.full_address, inputValue);
            const parts = parse(option.properties.full_address, matches);
            const latLngFormat = formatLatLng([
              option.geometry.coordinates[1],
              option.geometry.coordinates[0],
            ]);

            // eslint-disable-next-line react/prop-types
            const key = `${props.id}-${option.id}`;

            return (
              <li {...props} key={key}>
                <Grid container alignItems="center">
                  <Grid item xs>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: part.highlight ? 700 : 500,
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                    <Typography
                      color="grey.80"
                      fontWeight={400}
                      fontSize="0.875rem"
                    >
                      {latLngFormat}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      )}
    />
  );
}
